import { useState, useEffect, useMemo, useCallback } from "react";
import type { Appearance, Stripe } from "@stripe/stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { OrderQuantitySelector } from "./OrderQuantitySelector";
import { getContrastTextColor, mutedOnSurfaceHex } from "@/utils/colorUtils";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";

interface Product {
  _id: string;
  name?: string;
  /** Display / line math uses integer cents */
  price: number;
}

export type CheckoutPaymentCheckoutSettings = {
  tipping?: {
    enabled: boolean;
    amount1?: number;
    amount2?: number;
    amount3?: number;
  };
  colors?: {
    backgroundColor?: string;
    buttonColor?: string;
    accentColor?: string;
  };
} | null;

export type CheckoutSessionLineItemBody = {
  productId: string;
  quantity: number;
  variantId?: string;
};

interface CheckoutPaymentOrderAndTipsProps {
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  total: number;
  shippingCents?: number;
  shippingLabel?: string;
  shippingProtectionCents?: number;
  shippingProtectionSelectable?: boolean;
  shippingProtectionUnitCents?: number;
  shippingProtectionChecked?: boolean;
  onShippingProtectionChange?: (checked: boolean) => void;
  checkoutSettings: CheckoutPaymentCheckoutSettings;
  onHostedTipAmountCentsChange?: ({
    tipAmountCents,
  }: {
    tipAmountCents: number;
  }) => void;
  onTipCentsChange?: (tipCents: number) => void;
}

function CheckoutPaymentOrderAndTips({
  products,
  quantities,
  onQuantityChange,
  total,
  shippingCents = 0,
  shippingLabel = "",
  shippingProtectionCents = 0,
  shippingProtectionSelectable = false,
  shippingProtectionUnitCents = 0,
  shippingProtectionChecked = false,
  onShippingProtectionChange,
  checkoutSettings,
  onHostedTipAmountCentsChange,
  onTipCentsChange,
}: CheckoutPaymentOrderAndTipsProps) {
  const [showTip, setShowTip] = useState(false);
  const [selectedTipAmount, setSelectedTipAmount] = useState<number | null>(null);
  const [customTipAmount, setCustomTipAmount] = useState<string>("");

  const getTipAmount = useCallback(() => {
    if (!showTip) return 0;
    if (selectedTipAmount !== null) return selectedTipAmount;
    const customAmount = parseFloat(customTipAmount);
    return isNaN(customAmount) || customAmount < 0 ? 0 : customAmount;
  }, [showTip, selectedTipAmount, customTipAmount]);

  useEffect(() => {
    const tipCents = Math.round(getTipAmount() * 100);
    onHostedTipAmountCentsChange?.({ tipAmountCents: tipCents });
    onTipCentsChange?.(tipCents);
  }, [
    showTip,
    selectedTipAmount,
    customTipAmount,
    getTipAmount,
    onHostedTipAmountCentsChange,
    onTipCentsChange,
  ]);

  return (
    <>
      <OrderQuantitySelector
        products={products}
        quantities={quantities}
        onQuantityChange={onQuantityChange}
        total={total}
        shippingCents={shippingCents}
        shippingLabel={shippingLabel || undefined}
        shippingProtectionCents={shippingProtectionCents}
        shippingProtectionSelectable={shippingProtectionSelectable}
        shippingProtectionUnitCents={shippingProtectionUnitCents}
        shippingProtectionChecked={shippingProtectionChecked}
        onShippingProtectionChange={onShippingProtectionChange}
        tipAmount={showTip ? Math.round(getTipAmount() * 100) : 0}
        checkoutColors={checkoutSettings?.colors ?? null}
      />

      {checkoutSettings?.tipping?.enabled && (
        <div className="py-4 space-y-4">
          <h3 className="text-lg font-semibold">Add tip</h3>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="show-tip"
              checked={showTip}
              onCheckedChange={(checked) => {
                setShowTip(checked === true);
                if (checked) {
                  if (checkoutSettings.tipping?.amount1 !== undefined) {
                    setSelectedTipAmount(checkoutSettings.tipping.amount1);
                  }
                  setCustomTipAmount("");
                } else {
                  setSelectedTipAmount(null);
                  setCustomTipAmount("");
                }
              }}
            />
            <Label htmlFor="show-tip" className="cursor-pointer">
              Show your support with a tip
            </Label>
          </div>
          {showTip && (
            <div className="space-y-3 pl-6">
              <div className="flex gap-2 flex-wrap">
                {checkoutSettings.tipping?.amount1 !== undefined && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedTipAmount(checkoutSettings.tipping!.amount1!);
                      setCustomTipAmount("");
                    }}
                    style={
                      selectedTipAmount === checkoutSettings.tipping.amount1
                        ? {
                            backgroundColor: checkoutSettings.colors?.accentColor || "#4b5563",
                            color: getContrastTextColor(checkoutSettings.colors?.accentColor || "#4b5563"),
                          }
                        : undefined
                    }
                  >
                    ${checkoutSettings.tipping.amount1.toFixed(2)}
                  </Button>
                )}
                {checkoutSettings.tipping?.amount2 !== undefined && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedTipAmount(checkoutSettings.tipping!.amount2!);
                      setCustomTipAmount("");
                    }}
                    style={
                      selectedTipAmount === checkoutSettings.tipping.amount2
                        ? {
                            backgroundColor: checkoutSettings.colors?.accentColor || "#4b5563",
                            color: getContrastTextColor(checkoutSettings.colors?.accentColor || "#4b5563"),
                          }
                        : undefined
                    }
                  >
                    ${checkoutSettings.tipping.amount2.toFixed(2)}
                  </Button>
                )}
                {checkoutSettings.tipping?.amount3 !== undefined && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSelectedTipAmount(checkoutSettings.tipping!.amount3!);
                      setCustomTipAmount("");
                    }}
                    style={
                      selectedTipAmount === checkoutSettings.tipping.amount3
                        ? {
                            backgroundColor: checkoutSettings.colors?.accentColor || "#4b5563",
                            color: getContrastTextColor(checkoutSettings.colors?.accentColor || "#4b5563"),
                          }
                        : undefined
                    }
                  >
                    ${checkoutSettings.tipping.amount3.toFixed(2)}
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="custom-tip">Custom tip</Label>
                <Input
                  id="custom-tip"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={customTipAmount}
                  onChange={(e) => {
                    setCustomTipAmount(e.target.value);
                    setSelectedTipAmount(null);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function HostedPaymentElementBlock({
  paymentElementOptions,
  name,
  email,
  tipAmountCents,
  onFinalizeHostedCheckout,
  isProcessing,
  error,
  setError,
  setIsProcessing,
  buttonColorHex,
  total,
  shippingCents,
  shippingProtectionCents,
}: {
  paymentElementOptions: { layout: "tabs" };
  name: string;
  email: string;
  tipAmountCents: number;
  onFinalizeHostedCheckout?: (args: {
    tipAmountCents?: number;
    paymentIntentId?: string;
  }) => Promise<void>;
  isProcessing: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  buttonColorHex: string;
  total: number;
  shippingCents: number;
  shippingProtectionCents: number;
}) {
  const stripe = useStripe();
  const elements = useElements();

  const handlePay = async () => {
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`,
          payment_method_data: {
            billing_details: {
              name: name.trim() || undefined,
              email: email.trim() || undefined,
            },
          },
        },
        redirect: "if_required",
      });
      if (confirmErr) {
        setError(confirmErr.message ?? "Payment failed");
        setIsProcessing(false);
        return;
      }
      if (onFinalizeHostedCheckout) {
        await onFinalizeHostedCheckout({
          ...(tipAmountCents > 0 ? { tipAmountCents } : {}),
          ...(paymentIntent?.id ? { paymentIntentId: paymentIntent.id } : {}),
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="space-y-2">
        <div className="p-3 border border-gray-300 rounded-md">
          <PaymentElement options={paymentElementOptions} />
        </div>
      </div>
      {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
      <button
        type="button"
        className="w-full px-6 py-3 rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: buttonColorHex,
          color: getContrastTextColor(buttonColorHex),
        }}
        onClick={() => void handlePay()}
        disabled={
          !stripe ||
          isProcessing ||
          total + shippingCents + shippingProtectionCents === 0 ||
          !elements
        }
      >
        {isProcessing ? "Processing..." : "Place Order"}
      </button>
    </>
  );
}

export interface HostedCheckoutPaymentProps {
  stripePromise: Promise<Stripe | null>;
  clientSecret: string | null;
  sessionError: string | null;
  elementsKey: string;
  appearance: Appearance;
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  total: number;
  shippingCents?: number;
  shippingLabel?: string;
  shippingProtectionCents?: number;
  shippingProtectionSelectable?: boolean;
  shippingProtectionUnitCents?: number;
  shippingProtectionChecked?: boolean;
  onShippingProtectionChange?: (checked: boolean) => void;
  name: string;
  email: string;
  onHostedTipAmountCentsChange?: ({
    tipAmountCents,
  }: {
    tipAmountCents: number;
  }) => void;
  onFinalizeHostedCheckout?: (args: {
    tipAmountCents?: number;
    paymentIntentId?: string;
  }) => Promise<void>;
  isProcessing: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  checkoutSettings: CheckoutPaymentCheckoutSettings;
}

export function HostedCheckoutPayment({
  stripePromise,
  clientSecret,
  sessionError,
  elementsKey,
  appearance,
  products,
  quantities,
  onQuantityChange,
  total,
  shippingCents = 0,
  shippingLabel = "",
  shippingProtectionCents = 0,
  shippingProtectionSelectable = false,
  shippingProtectionUnitCents = 0,
  shippingProtectionChecked = false,
  onShippingProtectionChange,
  name,
  email,
  onHostedTipAmountCentsChange,
  onFinalizeHostedCheckout,
  isProcessing,
  error,
  setError,
  setIsProcessing,
  checkoutSettings,
}: HostedCheckoutPaymentProps) {
  const backgroundColor = checkoutSettings?.colors?.backgroundColor || "#ffffff";
  const textMuted = mutedOnSurfaceHex(backgroundColor);
  const paymentElementOptions = useMemo(
    () => ({
      layout: "tabs" as const,
    }),
    []
  );
  const buttonColorHex = checkoutSettings?.colors?.buttonColor || "#000000";
  const [reportedTipCents, setReportedTipCents] = useState(0);

  const preparingOrErrorMsg = sessionError?.trim() || null;

  return (
    <div className="space-y-4 flex-1">
      <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
        <CheckoutPaymentOrderAndTips
          products={products}
          quantities={quantities}
          onQuantityChange={onQuantityChange}
          total={total}
          shippingCents={shippingCents}
          shippingLabel={shippingLabel}
          shippingProtectionCents={shippingProtectionCents}
          shippingProtectionSelectable={shippingProtectionSelectable}
          shippingProtectionUnitCents={shippingProtectionUnitCents}
          shippingProtectionChecked={shippingProtectionChecked}
          onShippingProtectionChange={onShippingProtectionChange}
          checkoutSettings={checkoutSettings}
          onHostedTipAmountCentsChange={onHostedTipAmountCentsChange}
          onTipCentsChange={setReportedTipCents}
        />

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            {!clientSecret ? (
              <div className="p-3 border border-gray-300 rounded-md min-h-[140px] flex items-center justify-center px-3">
                {preparingOrErrorMsg ? (
                  <p className="text-sm text-red-500 text-center">{preparingOrErrorMsg}</p>
                ) : (
                  <p className="text-sm text-center" style={{ color: textMuted }}>
                    Preparing payment…
                  </p>
                )}
              </div>
            ) : (
              <Elements
                key={elementsKey}
                stripe={stripePromise}
                options={{ clientSecret, appearance }}
              >
                <HostedPaymentElementBlock
                  paymentElementOptions={paymentElementOptions}
                  name={name}
                  email={email}
                  tipAmountCents={reportedTipCents}
                  onFinalizeHostedCheckout={onFinalizeHostedCheckout}
                  isProcessing={isProcessing}
                  error={error}
                  setError={setError}
                  setIsProcessing={setIsProcessing}
                  buttonColorHex={buttonColorHex}
                  total={total}
                  shippingCents={shippingCents}
                  shippingProtectionCents={shippingProtectionCents}
                />
              </Elements>
            )}
          </div>
        </div>

        {!clientSecret ? (
          <button
            type="button"
            className="w-full px-6 py-3 rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: buttonColorHex,
              color: getContrastTextColor(buttonColorHex),
            }}
            disabled
          >
            Place Order
          </button>
        ) : null}
      </form>
    </div>
  );
}
